import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, Building2 } from 'lucide-react';
import { pricingPlans, allFeatures } from '../../data/mock';
import useReveal from '../../hooks/useReveal';

/**
 * Pricing - static, published price list.
 *
 * Three individual durations plus an Enterprise tier for colleges and teams.
 * Everything is included on every individual plan, so the feature list sits once
 * below the cards rather than being repeated in each; the only variable is how
 * long you want access for.
 */
const PricingSection = () => {
  const ref = useReveal({ stagger: 90 });

  return (
    <section className="section-alt">
      <div ref={ref} className="section">
        <header className="section-header">
          <p className="eyebrow reveal">Pricing</p>
          <h2 className="heading-2 reveal mt-3">Simple pricing, full access</h2>
          <p className="body-large reveal">
            Every plan unlocks all 11 modules, the AI interview simulator and TPI
            certification. Longer plans cost less per month.
          </p>
        </header>

        <div className="mx-auto grid max-w-[1080px] items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {pricingPlans.map((plan) => {
            const featured = plan.popular;
            const isEnterprise = plan.enterprise;

            return (
              <div
                key={plan.id}
                className="reveal relative flex flex-col rounded-[22px] p-6"
                style={{
                  background: featured ? 'var(--bg-ink)' : '#fff',
                  border: featured
                    ? '1px solid transparent'
                    : isEnterprise
                    ? '1px dashed var(--border-strong)'
                    : '1px solid var(--border-light)',
                  boxShadow: featured ? 'var(--shadow-xl)' : 'var(--shadow-sm)'
                }}
              >
                {featured && (
                  <span
                    className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[0.66rem] font-bold uppercase tracking-[0.1em]"
                    style={{ background: 'var(--accent-primary)', color: '#0b3d10' }}
                  >
                    <Sparkles className="h-3 w-3" />
                    Most popular
                  </span>
                )}

                {isEnterprise && (
                  <span
                    className="mb-3 grid h-10 w-10 place-items-center rounded-xl"
                    style={{ background: 'rgba(52,89,164,0.09)', color: 'var(--tenses-blue)' }}
                  >
                    <Building2 className="h-[18px] w-[18px]" />
                  </span>
                )}

                <h3
                  className="text-[1rem] font-semibold"
                  style={{ color: featured ? '#fff' : 'var(--text-primary)' }}
                >
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span
                    className="display leading-none"
                    style={{
                      fontSize: isEnterprise ? '1.75rem' : '2.3rem',
                      color: featured ? '#fff' : 'var(--text-primary)'
                    }}
                  >
                    {plan.price}
                  </span>
                  {plan.gstNote && (
                    <span
                      className="text-[0.78rem]"
                      style={{ color: featured ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)' }}
                    >
                      {plan.gstNote}
                    </span>
                  )}
                </div>

                <p
                  className="mt-2 text-[0.83rem]"
                  style={{ color: featured ? 'rgba(255,255,255,0.6)' : 'var(--text-secondary)' }}
                >
                  {isEnterprise ? (
                    plan.duration
                  ) : (
                    <>
                      {plan.perMonth} · {plan.priceWithGst} total
                    </>
                  )}
                </p>

                {isEnterprise && (
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {[
                      'Bulk licences for a whole batch',
                      'Groups, cohorts & analytics',
                      'Custom mock assessments',
                      'Dedicated support'
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-[0.86rem]"
                        style={{ color: 'var(--text-body)' }}
                      >
                        <span
                          className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full"
                          style={{ background: 'var(--accent-wash)' }}
                        >
                          <Check className="h-2.5 w-2.5" style={{ color: 'var(--accent-text)' }} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {/* CTA */}
                {isEnterprise ? (
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex min-h-[46px] w-full items-center justify-center rounded-full text-[0.92rem] font-semibold transition-transform duration-200 hover:-translate-y-0.5"
                    style={{
                      background: 'var(--tenses-blue)',
                      color: '#fff',
                      boxShadow: 'var(--shadow-blue)'
                    }}
                  >
                    {plan.cta}
                  </Link>
                ) : (
                  <a
                    href="https://app.tenses.ai"
                    className="mt-auto inline-flex min-h-[46px] w-full items-center justify-center rounded-full text-[0.92rem] font-semibold transition-transform duration-200 hover:-translate-y-0.5"
                    style={
                      featured
                        ? { marginTop: '1.75rem', background: '#fff', color: 'var(--tenses-blue-dark)' }
                        : {
                            marginTop: '1.75rem',
                            background: '#fff',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-medium)'
                          }
                    }
                  >
                    {plan.cta}
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* What every individual plan includes */}
        <div
          className="reveal mx-auto mt-12 max-w-[1080px] rounded-[22px] bg-white p-7 sm:p-9"
          style={{ border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-xs)' }}
        >
          <p
            className="mb-5 text-[0.72rem] font-semibold uppercase tracking-[0.14em]"
            style={{ color: 'var(--text-muted)' }}
          >
            Included in every plan
          </p>
          <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {allFeatures.map((feature) => (
              <li
                key={feature}
                className="flex items-start gap-2.5 text-[0.9rem]"
                style={{ color: 'var(--text-body)' }}
              >
                <span
                  className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full"
                  style={{ background: 'var(--accent-wash)' }}
                >
                  <Check className="h-2.5 w-2.5" style={{ color: 'var(--accent-text)' }} />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <p className="reveal mt-8 text-center text-[0.85rem]" style={{ color: 'var(--text-muted)' }}>
          All prices in INR, exclusive of 18% GST. Access begins as soon as your payment is
          confirmed. There is no free trial, and all sales are final - please see our{' '}
          <Link to="/terms" className="underline">Terms of Service</Link>.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
